import React, { useEffect, useState } from 'react'
import CompactKlimtWrapper from '@eversports/klimt-design-components/CompactKlimtWrapper'
import Stack from '@eversports/klimt-primitives/Stack'
import Link from '@eversports/klimt-primitives/Link'
import Button from '@eversports/klimt-primitives/Button'
import Text from '@eversports/klimt-primitives/Text'
import ShareIcon from '@eversports/design-tokens/assets/icons/share.svg'

import Icon from '@eversports/klimt-primitives/Icon'
import useConfirmationActions from '../../data/useConfirmationActions'

import { Localized } from '../../localization/react'
import Venue from '../../typings/Venue'
import InviteFriendsDialog from './InviteFriendsDialog'

interface Props {
  venue: Venue
  eventId?: string
  hasBooking: boolean
  invoiceDownloadLink?: string | null
  voucherCode?: string
  isVenueBrandingEnabled?: boolean
}

const InviteFriendsModalLocalStorageKey = 'hasSeenEventInviteFriendsModal'

const ConfirmationActions = ({
  venue,
  eventId,
  hasBooking,
  invoiceDownloadLink,
  voucherCode,
  isVenueBrandingEnabled = false,
}: Props) => {
  const [showInviteFriendsDialog, setShowInviteFriendsDialog] = useState(false)
  const { goToMyBookings, goBackToWebsite, goToMyRecording } = useConfirmationActions()
  const voucherDownloadLink = voucherCode ? `/widget/${venue.shortId}/voucher/code/${voucherCode}/html` : undefined

  useEffect(() => {
    let timer: null | ReturnType<typeof setTimeout> = null
    const hasSeenInviteFriendsModal = window.localStorage.getItem(InviteFriendsModalLocalStorageKey)

    if (!hasSeenInviteFriendsModal) {
      timer = setTimeout(() => setShowInviteFriendsDialog(true), 1000)
    }

    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [])

  const toggleDialog = () => {
    setShowInviteFriendsDialog(!showInviteFriendsDialog)
    if (!window.localStorage.getItem(InviteFriendsModalLocalStorageKey)) {
      window.localStorage.setItem(InviteFriendsModalLocalStorageKey, 'true')
    }
  }

  if (isVenueBrandingEnabled) {
    return (
      <CompactKlimtWrapper>
        <Stack
          alignItems="center"
          justifyContent="center"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={4}
          width="100%"
        >
          {voucherDownloadLink && (
            <Link to={voucherDownloadLink} external blank>
              <Button variant="primary" sx={{ flex: 1, width: '100%', maxWidth: '240px', padding: 2 }}>
                <Text sx={{ color: 'white' }}>
                  <Localized id="download-voucher" />
                </Text>
              </Button>
            </Link>
          )}
          {hasBooking && (
            <Link to={goToMyBookings} external>
              <Button variant="primary" sx={{ flex: 1, width: '100%', maxWidth: '240px', padding: 2 }}>
                <Text sx={{ color: 'white' }}>
                  <Localized id="confirmation-button-my-bookings" />
                </Text>
              </Button>
            </Link>
          )}
          {goToMyRecording && (
            <Link to={goToMyRecording} external>
              <Button variant="secondary" sx={{ flex: 1, width: '100%', maxWidth: '240px', padding: 2 }}>
                <Text sx={{ color: 'primary.main' }}>
                  <Localized id="confirmation-button-my-recording" />
                </Text>
              </Button>
            </Link>
          )}
          {!hasBooking && invoiceDownloadLink && (
            <Button
              variant={voucherDownloadLink ? 'secondary' : 'primary'}
              sx={{ flex: 1, width: '100%', maxWidth: '240px', padding: 2 }}
              onClick={() => window.open(invoiceDownloadLink, '_blank')}
            >
              <Text sx={{ color: voucherDownloadLink ? 'primary.main' : 'white' }}>
                <Localized id="download-invoice" />
              </Text>
            </Button>
          )}
        </Stack>
      </CompactKlimtWrapper>
    )
  }

  return (
    <CompactKlimtWrapper>
      <Stack alignItems="center" flexDirection={{ xs: 'column', md: 'row' }} gap={4} width="100%">
        {voucherDownloadLink && (
          <Link to={voucherDownloadLink} external blank>
            <Button variant="primary" sx={{ flex: 1, width: '100%', padding: 2 }}>
              <Text sx={{ color: 'white' }}>
                <Localized id="download-voucher" />
              </Text>
            </Button>
          </Link>
        )}
        {hasBooking && eventId && (
          <>
            <Button variant="primary" sx={{ flex: 1, width: '100%', padding: 2 }} onClick={toggleDialog}>
              <Stack direction="row" alignItems="center" gap={2}>
                <Icon size="small" color="white" src={ShareIcon} />
                <Text sx={{ color: 'white' }}>
                  <Localized id="confirmation-button-invite-friends" />
                </Text>
              </Stack>
            </Button>
            <InviteFriendsDialog isOpen={showInviteFriendsDialog} toggleDialog={toggleDialog} eventId={eventId} />
          </>
        )}
        <Link to={goBackToWebsite} external>
          <Button variant={hasBooking ? 'secondary' : 'primary'} sx={{ flex: 1, width: '100%', padding: 2 }}>
            <Text sx={{ color: hasBooking ? 'primary.main' : 'white' }}>
              <Localized id="confirmation-button-website" />
            </Text>
          </Button>
        </Link>
        {hasBooking && (
          <Link to={goToMyBookings} external>
            <Button variant="secondary" sx={{ flex: 1, width: '100%', padding: 2 }}>
              <Text sx={{ color: 'primary.main' }}>
                <Localized id="confirmation-button-my-bookings" />
              </Text>
            </Button>
          </Link>
        )}
        {goToMyRecording && (
          <Link to={goToMyRecording} external>
            <Button variant="secondary" sx={{ flex: 1, width: '100%', padding: 2 }}>
              <Text sx={{ color: 'primary.main' }}>
                <Localized id="confirmation-button-my-recording" />
              </Text>
            </Button>
          </Link>
        )}
        {!hasBooking && invoiceDownloadLink && (
          <Button
            variant="secondary"
            sx={{ flex: 1, width: '100%', padding: 2 }}
            onClick={() => window.open(invoiceDownloadLink, '_blank')}
          >
            <Text sx={{ color: 'primary.main' }}>
              <Localized id="download-invoice" />
            </Text>
          </Button>
        )}
      </Stack>
    </CompactKlimtWrapper>
  )
}

export default ConfirmationActions
